// Public, unauthenticated page describing how to delete an Iron Forums account.
// Google Play and the App Store both require a deletion URL that a reviewer can
// reach without installing the app or signing in, so this must stay outside the
// authenticated routes.

import { ArrowLeft, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../utils/support';
import BrandLogo from '../components/BrandLogo';

export default function DeleteAccountPage() {
  return (
    <div className="standalone-screen">
      <header className="bg-navy text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <BrandLogo variant="white" className="h-10 w-10" />
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <section className="bg-white rounded-2xl shadow-sm p-6 sm:p-10">
          <h1 className="text-3xl font-bold text-navy mb-4">Delete your Iron Forums account</h1>
          <p className="text-gray-700 leading-relaxed mb-8">
            You can delete your Iron Forums account and personal data at any time. There
            are two ways to do it.
          </p>

          <h2 className="text-xl font-bold text-navy mb-3">In the app</h2>
          <ol className="list-decimal pl-5 space-y-1 text-gray-700 leading-relaxed mb-8">
            <li>Sign in to Iron Forums.</li>
            <li>
              Open <span className="font-semibold">Settings</span> and scroll to{' '}
              <span className="font-semibold">Delete my account</span>.
            </li>
            <li>Confirm the request.</li>
          </ol>

          <h2 className="text-xl font-bold text-navy mb-3">By email</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            If you cannot sign in, email us from the address on your account and we will
            delete it for you.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20Iron%20Forums%20account`}
            className="inline-flex items-center gap-2 bg-navy text-white px-5 py-3 rounded-lg font-semibold hover:bg-navy-dark transition"
          >
            <Mail className="w-5 h-5" />
            {SUPPORT_EMAIL}
          </a>

          <h2 className="text-xl font-bold text-navy mt-10 mb-3">What happens next</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            You lose access immediately and cannot sign back in. We keep the account
            for a <span className="font-semibold">30-day grace period</span> so an
            admin can restore it if this was a mistake. After that, your account and
            personal data are permanently removed.
          </p>

          <h2 className="text-xl font-bold text-navy mt-8 mb-3">What is deleted</h2>
          <p className="text-gray-700 leading-relaxed mb-2">
            Your profile and contact details, snapshots, grow goals, messages, prayer
            requests, Ask IF conversations, uploaded photos, and notification settings.
          </p>

          <h2 className="text-xl font-bold text-navy mt-8 mb-3">What may be retained</h2>
          <p className="text-gray-700 leading-relaxed">
            We keep limited records where the law requires it or where we need them to
            resolve a dispute or enforce our agreements — for example, billing and
            transaction records. Backups are purged on their normal rotation. See{' '}
            <Link to="/privacy" className="text-navy underline hover:text-gold">
              Section 10 of our Privacy Policy
            </Link>{' '}
            for details.
          </p>
        </section>

        <div className="text-center mt-6 text-sm text-gray-500 space-x-4">
          <Link to="/privacy" className="text-navy underline hover:text-gold">
            Privacy Policy
          </Link>
          <Link to="/support" className="text-navy underline hover:text-gold">
            Support
          </Link>
        </div>
      </main>
    </div>
  );
}
